import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { domainAPI } from '../services/api';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiPlus, FiGlobe, FiCheck, FiX, FiRefreshCw, 
  FiCopy, FiExternalLink, FiTrash2, FiAlertCircle
} = FiIcons;

const CustomDomains = () => {
  const { currentWorkspace } = useWorkspace();
  const [domains, setDomains] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (currentWorkspace) {
      fetchDomains();
    }
  }, [currentWorkspace]);

  const fetchDomains = async () => {
    try {
      const data = await domainAPI.getDomains(currentWorkspace.id);
      setDomains(data);
    } catch (error) {
      console.error('Failed to fetch domains:', error);
    }
  };

  const handleAddDomain = async (e) => {
    e.preventDefault();
    if (!newDomain.trim()) return;

    setLoading(true);
    try {
      await domainAPI.addDomain(currentWorkspace.id, newDomain);
      setNewDomain('');
      setShowAddModal(false);
      fetchDomains();
    } catch (error) {
      console.error('Failed to add domain:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyDomain = async (domainId) => {
    try {
      await domainAPI.verifyDomain(currentWorkspace.id, domainId);
      fetchDomains();
    } catch (error) {
      console.error('Failed to verify domain:', error);
    }
  };

  const handleDeleteDomain = async (domainId) => {
    if (!confirm('Are you sure you want to delete this domain?')) return;

    try {
      await domainAPI.deleteDomain(currentWorkspace.id, domainId);
      fetchDomains();
    } catch (error) {
      console.error('Failed to delete domain:', error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // Show success message
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified':
        return FiCheck;
      case 'pending':
        return FiRefreshCw;
      case 'failed':
        return FiX;
      default:
        return FiAlertCircle;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Custom Domains</h1>
          <p className="mt-1 text-sm text-gray-500">
            Brand your QR code links with custom domains
          </p>
        </div>
        
        <button
          onClick={() => setShowAddModal(true)}
          className="mt-3 sm:mt-0 inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
          Add Domain
        </button>
      </div>

      {/* Domains List */}
      {domains.length > 0 ? (
        <div className="space-y-4">
          {domains.map((domain, index) => (
            <motion.div
              key={domain.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <SafeIcon icon={FiGlobe} className="w-5 h-5 text-blue-500 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-900">{domain.domain}</h3>
                    <span className={`ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(domain.status)}`}>
                      <SafeIcon icon={getStatusIcon(domain.status)} className="w-3 h-3 mr-1" />
                      {domain.status.charAt(0).toUpperCase() + domain.status.slice(1)}
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-500 mb-4">
                    Added on {new Date(domain.createdAt).toLocaleDateString()}
                  </p>

                  {domain.status !== 'verified' && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                      <h4 className="text-sm font-medium text-yellow-800 mb-2">
                        DNS Configuration Required
                      </h4>
                      <p className="text-sm text-yellow-700 mb-3">
                        Add these DNS records to your domain provider:
                      </p>
                      
                      <div className="space-y-2">
                        <div className="bg-white p-3 rounded border">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs font-medium text-gray-500">Type</p>
                              <p className="text-sm font-mono">CNAME</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500">Name</p>
                              <p className="text-sm font-mono">{domain.domain}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500">Value</p>
                              <p className="text-sm font-mono">qrsuite.pages.dev</p>
                            </div>
                            <button
                              onClick={() => copyToClipboard('qrsuite.pages.dev')}
                              className="p-1 text-gray-400 hover:text-gray-600"
                            >
                              <SafeIcon icon={FiCopy} className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {domain.status === 'verified' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center">
                        <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mr-2" />
                        <p className="text-sm text-green-700">
                          Domain is verified and ready to use
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  {domain.status !== 'verified' && (
                    <button
                      onClick={() => handleVerifyDomain(domain.id)}
                      className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Verify
                    </button>
                  )}
                  
                  {domain.status === 'verified' && (
                    <a
                      href={`https://${domain.domain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors inline-flex items-center"
                    >
                      <SafeIcon icon={FiExternalLink} className="w-4 h-4 mr-1" />
                      Visit
                    </a>
                  )}
                  
                  <button
                    onClick={() => handleDeleteDomain(domain.id)}
                    className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center"
        >
          <SafeIcon icon={FiGlobe} className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No custom domains</h3>
          <p className="text-gray-500 mb-6">
            Add your own domain to brand your QR code links and landing pages
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
            Add First Domain
          </button>
        </motion.div>
      )}

      {/* Benefits Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Benefits of Custom Domains
        </h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="flex items-start">
            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
              <SafeIcon icon={FiCheck} className="w-4 h-4 text-white" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900">Brand Recognition</h4>
              <p className="text-sm text-gray-600">Use your own domain for better brand trust</p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
              <SafeIcon icon={FiCheck} className="w-4 h-4 text-white" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900">Professional Links</h4>
              <p className="text-sm text-gray-600">Clean, professional URLs for your QR codes</p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
              <SafeIcon icon={FiCheck} className="w-4 h-4 text-white" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900">Better Analytics</h4>
              <p className="text-sm text-gray-600">Enhanced tracking with custom domains</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Add Domain Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 w-full max-w-md mx-4"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Add Custom Domain
            </h3>
            
            <form onSubmit={handleAddDomain}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Domain Name
                </label>
                <input
                  type="text"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  placeholder="qr.yourdomain.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter your subdomain (e.g., qr.yourdomain.com)
                </p>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setNewDomain('');
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !newDomain.trim()}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Adding...' : 'Add Domain'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default CustomDomains;